// Constantes e helpers de internacionalização do site público.
//
// Os 3 locales suportados batem com os aceitos pelo backend (`?lang=pt|en|es`).
// O default `pt` é usado quando o middleware não consegue detectar idioma do
// Accept-Language do browser.

export const LOCALES = ["pt", "en", "es"];
export const DEFAULT_LOCALE = "pt";

export function isLocale(x) {
  return LOCALES.includes(x);
}

// `<html lang>` segue convenção W3C: pt-BR é o português brasileiro
// (locale do conteúdo), en/es ficam em ISO-639 simples.
export function htmlLang(locale) {
  switch (locale) {
    case "pt":
      return "pt-BR";
    case "en":
      return "en";
    case "es":
      return "es";
    default:
      return "pt-BR";
  }
}

// Open Graph aceita formato `xx_YY` — useful pra og:locale.
export function ogLocale(locale) {
  switch (locale) {
    case "pt":
      return "pt_BR";
    case "en":
      return "en_US";
    case "es":
      return "es_ES";
    default:
      return "pt_BR";
  }
}

// Garante que um path "interno" (ex: "/sobre", "/pecas") sai com prefixo
// de locale ("/pt/sobre"). Idempotente — se já tem locale válido na frente,
// devolve igual. Preserva query string.
export function withLocale(path, locale) {
  const target = isLocale(locale) ? locale : DEFAULT_LOCALE;
  if (!path) return `/${target}`;
  const raw = String(path);
  if (/^https?:\/\//i.test(raw)) return raw; // externo
  if (raw.startsWith("#") || raw.startsWith("?")) return raw; // hash / query puro
  const [pathOnly, ...rest] = raw.split("?");
  const query = rest.length > 0 ? `?${rest.join("?")}` : "";
  if (pathOnly === "/") return `/${target}${query}`;
  const m = pathOnly.match(/^\/(pt|en|es)(\/|$)/);
  if (m) return `/${target}${pathOnly.slice(3)}${query}`; // troca segmento
  if (pathOnly.startsWith("/")) return `/${target}${pathOnly}${query}`;
  return `/${target}/${pathOnly}${query}`;
}

// Troca apenas o primeiro segmento de locale dentro de um pathname já
// renderizado (ex: "/pt/produto/eixo-2" → "/en/produto/eixo-2"). Usado pelo
// LanguageSwitcher.
export function swapLocale(pathname, targetLocale) {
  const target = isLocale(targetLocale) ? targetLocale : DEFAULT_LOCALE;
  if (!pathname || pathname === "/") return `/${target}`;
  const m = pathname.match(/^\/(pt|en|es)(\/|$)/);
  if (m) return `/${target}${pathname.slice(3) || "/"}`.replace(/\/$/, "/");
  return `/${target}${pathname.startsWith("/") ? "" : "/"}${pathname}`;
}

// Dado um caminho neutro (sem locale), constrói o objeto `alternates.languages`
// no formato esperado pelo Next Metadata API — usado pra emitir hreflang.
export function localeAlternates(neutralPath) {
  const safe = neutralPath && neutralPath.startsWith("/") ? neutralPath : `/${neutralPath ?? ""}`;
  const tail = safe === "/" ? "" : safe;
  return {
    "pt-BR": `/pt${tail}`,
    en: `/en${tail}`,
    es: `/es${tail}`,
    // x-default aponta pra versão PT (idioma principal do site).
    "x-default": `/pt${tail}`,
  };
}
